import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface GoogleIndexingHelperProps {
  debug?: boolean;
}

// Private routes that should never be indexed
const PRIVATE_PATHS = [
  '/dashboard', '/settings', '/projects', '/project/', '/admin/',
  '/ai-predictive', '/virtual-agent', '/analytics', '/resources',
  '/automation', '/innovation', '/genetics', '/reports',
  '/create-presentation', '/training', '/course/', '/video-course/',
  '/project-management/', '/project-planner', '/bulk-import',
  '/user-manual', '/team', '/project-members', '/task-track',
  '/alerts', '/orchestrator', '/marketing-agent', '/communication',
  '/executive-report', '/auth', '/profile', '/onboarding'
];

const isPrivateRoute = (pathname: string): boolean => {
  return PRIVATE_PATHS.some(p => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p));
};

const GoogleIndexingHelper: React.FC<GoogleIndexingHelperProps> = ({ debug = false }) => {
  const location = useLocation();
  const baseUrl = 'https://proyectmind.online';
  const isPrivate = isPrivateRoute(location.pathname);

  useEffect(() => {
    const canonicalUrl = `${baseUrl}${location.pathname}`;
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonicalLink);
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', canonicalUrl);
    }

    if (debug) {
      console.log('[SEO] Ruta:', location.pathname, isPrivate ? '(noindex)' : '(index)');
    }

    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
        page_path: location.pathname,
      });
    }
  }, [location.pathname, debug, isPrivate]);

  const prerenderHints = [
    '/features',
    '/pricing',
    '/blog',
    '/use-cases',
    '/comparativas',
    '/about-us',
    '/contact'
  ];

  return (
    <Helmet>
      {/* Noindex for private/app routes */}
      {isPrivate && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Prerender hints for public pages only */}
      {!isPrivate && prerenderHints.map(path => (
        <link key={path} rel="prerender" href={`${baseUrl}${path}`} />
      ))}
      
      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
      <link rel="dns-prefetch" href="https://llznalrdfowtbhqrxiaq.supabase.co" />
      
      <meta name="fragment" content="!" />
      <meta httpEquiv="content-language" content="es-MX" />
      <meta name="geo.region" content="MX" />
      <meta name="geo.placename" content="México" />
      <meta name="rating" content="general" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
    </Helmet>
  );
};

export default GoogleIndexingHelper;

export const useDynamicMeta = () => {
  const updateMeta = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`) ||
               document.querySelector(`meta[property="${name}"]`);
    
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      if (name.startsWith('og:')) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };

  const updateTitle = (title: string) => {
    document.title = title;
    updateMeta('og:title', title);
    updateMeta('twitter:title', title);
  };

  const updateDescription = (description: string) => {
    updateMeta('description', description);
    updateMeta('og:description', description);
    updateMeta('twitter:description', description);
  };

  return { updateMeta, updateTitle, updateDescription };
};

export const NotFoundSEO: React.FC = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
    <meta name="prerender-status-code" content="404" />
    <title>Página no encontrada | ProyectMind</title>
  </Helmet>
);
